import { IconComponent } from "@@common";
import { Box, Link as MuiLink } from "@mui/material";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useComponentVisibility from "../../Hooks/useComponentVisibility";
import useObservedText from "../../Hooks/useObservedText";
import { LinkModel } from "../../Models/LinkModel";
import { IconPlacement } from "../../Models/TypographyModel";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const isAbsoluteUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  return url.includes("://");
};

const LinkComponent: React.FC<IComponentProps<LinkModel>> = ({ component, context, componentPath = [] }) => {
  const componentLayoutSx = getComponentLayout(component);
  const selectionProps = useComponentSelection({ component, componentPath });

  const text = useObservedText(component.Text, context, "LinkComponent");
  const href = useObservedText(component.HrefTemplate, context, "LinkComponent");
  const { isVisible, hiddenSx } = useComponentVisibility(component, context, "LinkComponent");

  if (!isVisible) {
    return null;
  }

  const hasIcon = Boolean(component.IconName);
  const iconPlacement = component.IconPlacement || IconPlacement.Start;

  const isAbsolute = isAbsoluteUrl(href);
  const linkProps = isAbsolute ? { href: href ?? "#" } : { component: RouterLink, to: href ?? "/" };

  const iconElement = hasIcon ? (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
      <IconComponent iconName={component.IconName!} />
    </Box>
  ) : null;

  return (
    <MuiLink
      {...linkProps}
      variant={component.Variant}
      target={component.Target}
      sx={{
        ...componentLayoutSx,
        textDecoration: "none",
        ...(hasIcon && { display: "inline-flex", alignItems: "center", gap: 1 }),
        ...(IsDesignMode && { minHeight: "10px" }),
        ...hiddenSx,
      }}
      {...selectionProps}
    >
      {iconPlacement === IconPlacement.Start && iconElement}
      {text}
      {iconPlacement === IconPlacement.End && iconElement}
    </MuiLink>
  );
};

export default LinkComponent;
